import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { PrintNodeService } from '../print-node.service'
import { PrinterSearchParams } from '../models/print-node-search.model'
import { Computer } from '../models/print-node.entity'
import { Page } from '../../../models/util.model'

interface PrintersListingState {
  filters?: PrinterSearchParams
  computers?: Computer[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  searchKeys?: PrinterSearchParams
  customFilters?: PrinterSearchParams
}

const PRINTERS_LISTING_INITIAL_STATE: PrintersListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class PrintersListingStore {
  constructor(
    private printService: PrintNodeService,
    // private printersRepository: PrintersRepository,
  ) {}

  private state = signal<PrintersListingState>(PRINTERS_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly computers = computed(() => this.state().computers || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly searchKeys = computed(() => this.state().searchKeys)

  // Effects

  async search(params?: PrinterSearchParams): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.printService.searchComputers$(params).subscribe({
        next: (data) => {
          resolve()
          this.setData(data)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(data: Page<Computer>) {
    this.state.set({
      ...this.state(),
      computers: data.data,
      totalCount: data.totalCount,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: PrinterSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: PrinterSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: PrinterSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof PrinterSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setSearchKeys(searchKeys?: PrinterSearchParams) {
    this.state.set({ ...this.state(), searchKeys })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    state: PrintersListingState,
    filters?: PrinterSearchParams,
  ): PrinterSearchParams {
    let params: PrinterSearchParams = filters || {}
    if (state.customFilters) {
      params = { ...params, ...state.customFilters }
    }
    if (state.searchKeys) {
      params = { ...params, ...state.searchKeys }
    }

    return params
  }
}
